/**
 * TransitionManager.ts — Consistent screen transition handling.
 *
 * Provides utilities for clean transitions between scenes and content blocks.
 * Prevents content overlap by managing screen clearing and status bar preservation.
 */

import type { Terminal } from './Terminal';
import type { TextEffects } from './TextEffects';
// ColorName reserved for future color-based transitions
// import type { ColorName } from './ColorTheme';

export interface TransitionOptions {
    /** Clear entire screen before transition */
    clearScreen?: boolean;
    /** Show brief transition effect */
    showEffect?: boolean;
    /** Preserve status bar during transition */
    preserveStatusBar?: boolean;
    /** Rows to preserve at top (e.g., status bar height) */
    reserveTopRows?: number;
    /** Rows to preserve at bottom (e.g., panel height) */
    reserveBottomRows?: number;
}

/** Default transition options */
const DEFAULT_OPTIONS: TransitionOptions = {
    clearScreen: true,
    showEffect: false,
    preserveStatusBar: false,
    reserveTopRows: 0,
    reserveBottomRows: 0,
};

export class TransitionManager {
    private terminal: Terminal;
    private effects: TextEffects;

    constructor(terminal: Terminal, effects: TextEffects) {
        this.terminal = terminal;
        this.effects = effects;
    }

    /**
     * Prepare screen for new content.
     * Clears appropriate regions while preserving status bars if requested.
     */
    async prepareTransition(options: TransitionOptions = {}): Promise<void> {
        const opts = { ...DEFAULT_OPTIONS, ...options };

        if (opts.showEffect) {
            await this.fadeTransition();
        }

        if (opts.clearScreen) {
            if (opts.preserveStatusBar || opts.reserveTopRows || opts.reserveBottomRows) {
                // Clear only the content area
                this.clearContentArea(opts.reserveTopRows, opts.reserveBottomRows);
            } else {
                // Full screen clear
                this.terminal.clear();
            }
        }
    }

    /**
     * Clear content area while preserving status bars.
     * @param reserveTop - Number of rows to preserve at top
     * @param reserveBottom - Number of rows to preserve at bottom
     */
    clearContentArea(reserveTop = 0, reserveBottom = 0): void {
        const { rows } = this.terminal.getSize();
        const startRow = reserveTop + 1;
        const endRow = rows - reserveBottom;

        if (startRow <= endRow) {
            this.terminal.clearRegion(startRow, endRow);
            // Position cursor at start of content area
            this.terminal.setCursor(startRow, 1);
        }
    }

    /**
     * Clear everything below a specific row.
     * Useful for clearing narrative content before showing a menu.
     * @param fromRow - First row to clear (1-based)
     * @param reserveBottom - Rows to preserve at bottom
     */
    clearBelow(fromRow: number, reserveBottom = 0): void {
        const { rows } = this.terminal.getSize();
        const endRow = rows - reserveBottom;

        if (fromRow <= endRow) {
            this.terminal.clearRegion(fromRow, endRow);
        }
    }

    /**
     * Show a brief loading indicator.
     * @param message - Loading message to display
     * @param durationMs - How long to show (default 500ms)
     */
    async showLoading(message = 'Loading...', durationMs = 500): Promise<void> {
        const { rows } = this.terminal.getSize();
        const centerRow = Math.floor(rows / 2);

        // Save cursor position
        this.terminal.writeRaw('\x1b[s');

        // Show loading message centered
        this.terminal.setCursor(centerRow, 1);
        this.terminal.writeRaw('\x1b[2K'); // Clear line
        const { cols } = this.terminal.getSize();
        const padding = Math.max(0, Math.floor((cols - message.length) / 2));
        this.terminal.write(' '.repeat(padding) + message, 'khaki');

        await this.effects.delay(durationMs);

        // Clear and restore cursor
        this.terminal.setCursor(centerRow, 1);
        this.terminal.writeRaw('\x1b[2K');
        this.terminal.writeRaw('\x1b[u');
    }

    /**
     * Brief fade transition effect using CSS opacity.
     * Creates a visual break between content. Respects prefers-reduced-motion.
     */
    async fadeTransition(durationMs = 150): Promise<void> {
        // Respect reduced motion preference
        if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const container = typeof document !== 'undefined'
            ? document.getElementById('terminal-container')
            : null;

        if (container) {
            container.style.transition = `opacity ${durationMs}ms ease-in-out`;
            container.style.opacity = '0.3';
            await this.effects.delay(durationMs);
            container.style.opacity = '1';
            await this.effects.delay(durationMs);
            container.style.transition = '';
        } else {
            await this.effects.delay(durationMs);
        }
    }

    /**
     * Show a spinner animation for indeterminate progress.
     * @param message - Message to show alongside spinner
     * @param durationMs - How long to spin
     */
    async showSpinner(message: string, durationMs: number): Promise<void> {
        const frames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
        const frameInterval = 80;
        const iterations = Math.ceil(durationMs / frameInterval);

        const { rows, cols } = this.terminal.getSize();
        const centerRow = Math.floor(rows / 2);
        const fullMessage = `  ${message}`;
        const padding = Math.max(0, Math.floor((cols - fullMessage.length - 2) / 2));

        // Save cursor
        this.terminal.writeRaw('\x1b[s');
        this.terminal.hideCursor();

        for (let i = 0; i < iterations; i++) {
            const frame = frames[i % frames.length];
            this.terminal.setCursor(centerRow, 1);
            this.terminal.writeRaw('\x1b[2K');
            this.terminal.write(' '.repeat(padding), 'white');
            this.terminal.write(frame, 'amber');
            this.terminal.write(fullMessage, 'khaki');

            await new Promise(resolve => setTimeout(resolve, frameInterval));
        }

        // Clear spinner line and restore
        this.terminal.setCursor(centerRow, 1);
        this.terminal.writeRaw('\x1b[2K');
        this.terminal.showCursor();
        this.terminal.writeRaw('\x1b[u');
    }

    /**
     * Transition to a new scene with optional loading indicator.
     * @param sceneName - Name of scene for loading message
     * @param options - Transition options
     */
    async transitionToScene(sceneName?: string, options: TransitionOptions = {}): Promise<void> {
        const opts = { ...DEFAULT_OPTIONS, ...options };

        if (opts.showEffect && sceneName) {
            await this.showLoading(`Loading ${sceneName}...`, 300);
        }

        await this.prepareTransition(opts);
    }
}
